import type { Exercise } from '@fluent/types';
import type { MatchVerdict } from './matchAnswer.js';
import { TranslateTyped } from './TranslateTyped.js';
import { TranslateTap } from './TranslateTap.js';
import { ListenType } from './ListenType.js';
import { TapPairs } from './TapPairs.js';
import { PhraseIntro } from './PhraseIntro.js';
import { GrammarTip } from './GrammarTip.js';
import { Dialogue } from './Dialogue.js';
import { ChooseCorrect } from './ChooseCorrect.js';
import { ListenChoose } from './ListenChoose.js';
import { Cloze } from './Cloze.js';
import { PhraseChoice } from './PhraseChoice.js';

export interface ExerciseSlotProps {
  current: Exercise;
  tapPool: readonly string[] | null;
  choicePool: readonly string[] | null;
  tapIndices: number[];
  setTapIndices: (next: number[]) => void;
  typedValue: string;
  setTypedValue: (v: string) => void;
  selectedOption: string | null;
  setSelectedOption: (next: string) => void;
  onPairsComplete: (summary: { hadWrong: boolean }) => void;
  onEnter: () => void;
  locked: boolean;
  lastVerdict: MatchVerdict | null;
}

/**
 * ExerciseSlot — the per-exercise-type rendering switch, shared by every
 * player that replays exercises (LessonPlayer today). Pure presentation: the owning player holds the live answer state
 * (typed text, tap indices, selected option) and passes it down with its
 * setters plus the tap_pairs completion callback. Extracted verbatim from
 * LessonPlayer so both players render byte-identical exercise DOM.
 */
export function ExerciseSlot({
  current,
  tapPool,
  choicePool,
  tapIndices,
  setTapIndices,
  typedValue,
  setTypedValue,
  selectedOption,
  setSelectedOption,
  onPairsComplete,
  onEnter,
  locked,
  lastVerdict,
}: ExerciseSlotProps) {
  switch (current.type) {
    case 'grammar_tip':
      return <GrammarTip exercise={current} locked={locked} />;
    case 'phrase_intro':
      return <PhraseIntro exercise={current} locked={locked} />;
    case 'dialogue':
      return <Dialogue exercise={current} locked={locked} />;
    case 'translate_typed':
      return (
        <TranslateTyped
          exercise={current}
          value={typedValue}
          onChange={setTypedValue}
          onEnter={onEnter}
          locked={locked}
          lastVerdict={lastVerdict}
        />
      );
    case 'listen_type':
      return (
        <ListenType
          exercise={current}
          value={typedValue}
          onChange={setTypedValue}
          onEnter={onEnter}
          locked={locked}
          lastVerdict={lastVerdict}
        />
      );
    case 'translate_tap':
      return tapPool ? (
        <TranslateTap
          exercise={current}
          pool={tapPool}
          selectedIndices={tapIndices}
          onSelectedIndicesChange={setTapIndices}
          locked={locked}
          lastVerdict={lastVerdict}
        />
      ) : null;
    case 'tap_pairs':
      return <TapPairs exercise={current} locked={locked} onComplete={onPairsComplete} />;
    case 'choose_correct':
      return (
        <ChooseCorrect
          exercise={current}
          options={choicePool ?? current.options}
          selected={selectedOption}
          onSelectedChange={setSelectedOption}
          locked={locked}
          lastVerdict={lastVerdict}
        />
      );
    case 'listen_choose':
      return (
        <ListenChoose
          exercise={current}
          options={choicePool ?? current.options}
          selected={selectedOption}
          onSelectedChange={setSelectedOption}
          locked={locked}
          lastVerdict={lastVerdict}
        />
      );
    case 'cloze':
      return (
        <Cloze
          exercise={current}
          options={choicePool ?? current.options}
          selected={selectedOption}
          onSelectedChange={setSelectedOption}
          locked={locked}
          lastVerdict={lastVerdict}
        />
      );
    case 'phrase_choice':
      return (
        <PhraseChoice
          exercise={current}
          options={choicePool ?? current.options}
          selected={selectedOption}
          onSelectedChange={setSelectedOption}
          locked={locked}
          lastVerdict={lastVerdict}
        />
      );
  }
}
